<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import type { CaseStudySladkaDilnaProps } from "./CaseStudySladkaDilnaProps.js";

	const _: CaseStudySladkaDilnaProps = $props();
	const { tenant } = getGlobalTemplateProps();
</script>

<Layout>
	<Section gridHeadline="container-start-2 container-end-10" gridChildren="container-start-2 container-end-9">
		{#snippet headline()}
			<Formatted
				text="E-shop a interní systém na míru do 3&nbsp;měsíců. Jak jsme Sladké dílně pomohli cg[[zvýšit počet objednávek o 50&nbsp;%]]?"
			/>
		{/snippet}

		<article>
			<p class="text-2md lg:text-lg max-w-4xl leading-[1.35em] mt-8">
				<i>
					<Formatted
						text="„Potřebujeme nový e-shop a interní systém, ale žádné z vyzkoušených řešení nám nevyhovuje,“"
					/>
				</i>
				<Formatted
					text=" prozradili nám v březnu 2024 na online schůzce majitelé [https://www.sladkadilna.cz][[cukrárny Sladká dílna]]. A tak jsme ve Fastest dali hlavy dohromady a přijali výzvu vytvořit v krátkém čase e-shop a systém na míru, díky kterému klient digitalizuje objednávky a sjednotí všechny klíčové procesy cukrářské výroby i prodeje „pod jednu střechu“."
				/>
			</p>

			<cite>
				<p class="max-w-2xl text-fstonyx">
					<Formatted
						text="„Průběh spolupráce byl hladký, rychlý a často i zábavný. Martin Kučera nás procesem trpělivě provedl a vše nám vysvětlil.“"
					/>
					<small class="block">
						<Formatted text="– Andrej a Sandra, nl[[]]majitelé Sladké dílny" />
					</small>
				</p>
			</cite>

			<h2 class="mt-16 mb-4">
				<Formatted text="O klientovi" />
			</h2>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="[https://www.sladkadilna.cz][[Sladká dílna]] je rodinná cukrárna z Prahy, která se specializuje na výrobu sladkých dobrot z prvotřídních surovin. V její nabídce najdete kromě klasických dortů a zákusků také minidezerty nebo svatební dorty, přičemž většinu objednávek dokáže připravit do druhého dne. V hlavním městě má dvě provozovny."
				/>
			</p>

			<h2 class="mt-16 mb-4">
				<Formatted text="Klíčové informace o zakázce" />
			</h2>
			<ul class="custom-list">
				<li>
					<Formatted text="sp[[Klient:]] Sladká dílna (www.sladkadilna.cz)" />
				</li>
				<li>
					<Formatted text="sp[[Projekt:]] E-shopové řešení s interním systémem a B2B portálem na míru" />
				</li>
				<li>
					<Formatted text="sp[[Období realizace:]] 03/2024–06/2024 (spolupráce pokračuje doposud)" />
				</li>
				<li>
					<Formatted
						text="sp[[Použité technologie:]] AWS, Svelte, DynamoDB, AppSync, GraphQL, Tailwind, Fakturoid, GoPay, Google Analytics 4, Smartlook, Sentry"
					/>
				</li>
			</ul>

			<h2 class="mt-16 mb-4">
				<Formatted text="„Zvládnete to do 3 měsíců?“" />
			</h2>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Zadání od Sladké dílny mělo jeden háček: klient potřeboval mít první životaschopnou/pilotní verzi produktu (MVP) připravenou nejpozději v červnu, tedy 3 měsíce od zaslání poptávky. Jestli je to možné stihnout? S Fastest rozhodně!"
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Cíl byl jasný: vytvořit e-shop a interní systém na míru, který bude sloužit ke správě objednávek, tisku štítků na krabičky nebo k získání přehledu o skladových zásobách. "
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Zkrátka jsme měli za úkol pomocí systému a e-shopu zajistit plynulý proces prodeje i výroby dobrot – tzv. „pod jednou střechou“ a samozřejmě v online prostředí. To mělo zamezit například chaotickému zapisování objednávek na papírky a dalším manuálním úkonům."
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Majitelé potřebovali také specifický portál pro B2B spolupráci, takže zakázka sestávala celkem ze 3 částí:"
				/>
			</p>
			<ul class="custom-list">
				<li>
					<Formatted text="E-shop pro online prodej" />
				</li>
				<li>
					<Formatted text="Interní systém" />
				</li>
				<li>
					<Formatted text="B2B portál pro spolupracující kavárny a cukrárny" />
				</li>
			</ul>

			<cite>
				<p class="max-w-2xl text-fstonyx">
					<Formatted
						text="„Průběh spolupráce byl hladký, rychlý a často i zábavný. Martin Kučera nás procesem trpělivě provedl a vše nám vysvětlil.“"
					/>
					<small class="block">
						<Formatted text="– Andrej a Sandra, nl[[]]majitelé Sladké dílny" />
					</small>
				</p>
			</cite>
			<h2 class="mt-16 mb-4">
				<Formatted text="Úvodní setkání, návrh řešení nl[[]]a hurá do práce" />
			</h2>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Na úvodní schůzce 19. 3. jsme si s klientem definovali zadání i rozpočet a již 22. 3. od nás Sladká dílna dostala návrh řešení."
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Po schválení ze strany klienta a odsouhlasení detailů se tým Fastest s projektem detailně seznámil a začal na něm „vyšívat“. Poznání podnikání Sladké dílny bylo zásadní, protože jen tak jsme mohli systém přizpůsobit klientovým potřebám."
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Práce na zakázce jsme pro plynulý průběh rozdělili do týdenních sprintů a po celou dobu byli s klientem v kontaktu. Nejprve přišla na řadu vývojová část (celý vývoj probíhal na cloudu AWS), až později grafické zpracování."
				/>
			</p>

			<cite>
				<p class="max-w-2xl text-fstonyx">
					<Formatted
						text="„Mohli jsme se v průběhu zakázky podílet na každém detailu, což je pro nás jako pro podnik se specifickými požadavky důležité. Nutno podotknout, že tým Fastest se rychle nacítil na problematiku našeho provozu, a tak jsme vše zvládli online. Ocenit musíme i analytické myšlení, které nás doslova posadilo a utvrdilo, že jsme ve správných rukou.“"
					/>
					<small class="block">
						<Formatted text="– Andrej a Sandra, nl[[]]majitelé Sladké dílny" />
					</small>
				</p>
			</cite>

			<h2 class="mt-16 mb-4">
				<Formatted text="Správa objednávek, automatizované účetnictví i přehled pro management na jednom místě" />
			</h2>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Nakonec to netrvalo ani celé 3 měsíce, Sladká dílna se mohla z pilotního výstupu těšit už 5. 6. Co vše tehdy e-shopové řešení a systém na míru umí?"
				/>
			</p>

			<h3 class="mt-16 mb-4">
				<Formatted text="Interní systém" />
			</h3>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Jednalo se o nejkomplexnější část projektu. Systém je vytvořený tak, aby usnadnil procesy napříč podnikáním. Pracuje s ním mimo jiné obsluha v cukrárně (objednávky na místě), manažeři poboček (sledují např. vytíženost poboček či výroby), vedoucí cukrářka (např. sestavování produktů a receptur), ale také cukráři ve výrobě, řidiči (přehledné zásobování poboček) a marketingový specialista (správa voucherů nebo práce s marketingovými nástroji)."
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted text="Součástí interního systému tedy je:" />
			</p>
			<div class="flex flex-col md:flex-row md:gap-16">
				<ul class="custom-list">
					<li><Formatted text="Správa objednávek" /></li>
					<li><Formatted text="Tisk štítků na krabičky" /></li>
					<li><Formatted text="Správa skladových zásob" /></li>
					<li><Formatted text="Modul pro výrobnu" /></li>
					<li><Formatted text="Modul pro provozovny" /></li>
					<li><Formatted text="Přehledy pro management" /></li>
				</ul>
				<ul class="custom-list">
					<li><Formatted text="Automatizované účetnictví a platby" /></li>
					<li><Formatted text="Správa voucherů" /></li>
					<li>
						<Formatted text="Správa speciálních poptávek" />
					</li>
					<li><Formatted text="Audit úprav dat v systému" /></li>
					<li><Formatted text="Správa uživatelů a oprávnění" /></li>
					<li><Formatted text="Modul pro řidiče" /></li>
				</ul>
			</div>

			<h3 class="mt-16 mb-4">
				<Formatted text="B2B portál" />
			</h3>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted text="Tento portál slouží ke komunikaci a spolupráci se partnerskými kavárnami a cukrárnami." />
			</p>
			<cite>
				<p class="max-w-2xl text-fstonyx">
					<Formatted
						text="„Interní systém nám teď ohromně šetří čas. Už nemusíme každou objednávku zapisovat ručně a neděláme chyby ve jménech nebo v telefonních číslech. Vše za nás řeší počítač.“"
					/>
					<small class="block">
						<Formatted text="– Andrej a Sandra, nl[[]]majitelé Sladké dílny" />
					</small>
				</p>
			</cite>

			<h3 class="mt-16 mb-4">
				<Formatted text="Po spuštění vzrostl počet nl[[]]objednávek o 50 % " />
			</h3>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="E-shop i interní systém se podařilo dokončit podle představ klienta a v požadovaném termínu do 3 měsíců."
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Po spuštění nového e-shopu Sladké dílně vzrostl počet objednávek o 50 %. Nárůst byl dokonce takový, že nás majitelé poprosili o vyvinutí funkce „záchranná brzda“, která znemožňuje další objednávky v případě velkého vytížení cukrárny."
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Na vývoji se Sladkou dílnou spolupracujeme i nadále, například do podzimu 2024 jsme systém doplnili o další funkcionality."
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted
					text="Ve Fastest máme velkou radost, že výsledek splnil očekávání a že se nám povedlo tuto velkou výzvu zvládnout. Na úspěšném dokončení projektu v termínu má podíl i skvěle spolupracující klient."
				/>
			</p>
			<p class="max-w-3xl text-md leading-[1.5em]">
				<Formatted text="Děkujeme za příležitost a za výbornou zkušenost!" />
			</p>
		</article>
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
			>
				Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
